import type { Store } from '@reduxjs/toolkit'
import { currentUserReducerMapObj } from '~/state/currentUser/reducer'
import { fileMetadataReducerMapObj } from '~/state/fileMetadata/reducer'
import { filesReducerMapObj } from '~/state/files/reducer'
import { jobInfoReducerMapObj } from '~/state/jobInfo/reducer'
import type { TimelineSectionReference } from '~/state/timeline'
import {
    TimelineSectionReference__asDays,
    TimelineSectionReference__asString,
    TimelineSectionReference__compare,
} from '~/state/timeline'
import { timelineReducerMapObj } from '~/state/timeline/reducers'
import { getTimelineJobID, getUnfetchedTimelineSections } from '~/state/timeline/selectors'
import { tranformIntoCachedInArray } from '~/utilities/arrayUtils'
import { getFileMonth } from '~/utilities/dateOperations'

import type { FetchFileRangeMethod } from '../job'
import {
    fetchDefaultJobID,
    fetchTimelineFileRange,
    fetchTimelineMonths,
    getTimelineFilesByIds,
} from '../job'

export const timelineChunkSyncerReducerMapObj = {
    ...timelineReducerMapObj,
    ...currentUserReducerMapObj,
    ...filesReducerMapObj,
    ...jobInfoReducerMapObj,
    ...fileMetadataReducerMapObj,
}

type TimelineChunkSyncerState = StateOfReducerMapObj<typeof timelineChunkSyncerReducerMapObj>

export class TimelineChunkSyncer {
    constructor(
        private store: Store<TimelineChunkSyncerState>,
        private fetchRange: FetchFileRangeMethod,
    ) {}

    public async setCurrentVisibleRanges(visibleSections: TimelineSectionReference[]) {
        const state = this.store.getState()

        if (state === undefined) {
            console.warn('TimelineChunkSyncer: undefined state.')
            return
        }

        const jobID = getTimelineJobID(state)

        const isUnfetched = tranformIntoCachedInArray(
            getUnfetchedTimelineSections(state),
            TimelineSectionReference__asString,
        )

        const ranges2fetch = visibleSections
            .filter(isUnfetched)
            .sort(TimelineSectionReference__compare)

        if (jobID && ranges2fetch.length > 0) {
            const newest = ranges2fetch[0]
            const oldest = ranges2fetch[ranges2fetch.length - 1]
            await this.fetchRange(
                this.store.dispatch,
                jobID,
                TimelineSectionReference__asDays(oldest).start,
                TimelineSectionReference__asDays(newest).end,
            )
        }
    }

    public async setCurrentVisibleRangesForFile(fileID: FileID, visiblePadding: number = 4) {
        const timelineID = await fetchDefaultJobID(this.store.dispatch)
        const months = await fetchTimelineMonths(this.store.dispatch, timelineID)

        const files = await getTimelineFilesByIds(this.store.dispatch, [fileID])

        if (files !== undefined && files.length > 0) {
            const file = files[0]
            const fileMonth = getFileMonth(file)

            const monthIndex = months.findIndex(
                (m) => m.year === fileMonth.year && m.month === fileMonth.month,
            )
            const leftVisibleMonths: MonthRef[] = []
            let leftCount = 0

            for (let i = monthIndex - 1; i >= 0; i--) {
                const currentMonth = months[i]
                leftCount += currentMonth.picture_count + currentMonth.video_count
                if (leftCount > visiblePadding) {
                    leftVisibleMonths.unshift(currentMonth)
                    break
                }
            }

            const rightVisibleMonths: MonthRef[] = []
            let rightCount = 0

            for (let i = monthIndex + 1; i < months.length; i++) {
                const currentMonth = months[i]
                rightCount += currentMonth.picture_count + currentMonth.video_count
                if (rightCount > visiblePadding) {
                    rightVisibleMonths.push(currentMonth)
                    break
                }
            }

            const monthsToFetch = [...leftVisibleMonths, months[monthIndex], ...rightVisibleMonths]
                .map((m) => ({
                    year: m.year,
                    month: m.month,
                    startDay: 1,
                    endDay: 31,
                }))
                .sort(TimelineSectionReference__compare)

            return this.fetchRange(
                this.store.dispatch,
                timelineID,
                TimelineSectionReference__asDays(monthsToFetch[monthsToFetch.length - 1]).start,
                TimelineSectionReference__asDays(monthsToFetch[0]).end,
            )
        }
    }
}

let instance: TimelineChunkSyncer
export const connectTimelineChunkSyncer = (store: Store) => {
    instance = new TimelineChunkSyncer(store, fetchTimelineFileRange)
}

export const getConnectedTimelineChunkSyncer = () => {
    if (instance === undefined) {
        throw 'getConnectedTimelineChunkSyncer ignored: Must be connected first'
    }

    return instance
}
